import { useEffect, useState } from "react";
import { themeOptionsForMode, type ThemeOption } from "./theme-options";

interface PublicImageTheme {
  code: string;
  name: string;
  description: string;
  origin: "builtin" | "custom" | "builtin_override";
  accent: string;
  background: string;
  revision?: number;
  coverUrl?: string;
}

let cache: PublicImageTheme[] | undefined;
let cacheUpdatedAt = 0;
let request: Promise<PublicImageTheme[]> | undefined;
export const IMAGE_THEME_CATALOG_CACHE_TTL_MS = 15_000;
const IMAGE_THEME_CATALOG_INVALIDATED_EVENT = "df:image-theme-catalog-invalidated";
const IMAGE_THEME_CATALOG_INVALIDATED_STORAGE_KEY = "pptdeep:image-theme-catalog-invalidated-at";

export function useImageThemeOptions(): ThemeOption[] {
  const [themes, setThemes] = useState<PublicImageTheme[] | undefined>(cache);
  useEffect(() => {
    let cancelled = false;
    const refresh = (force = false) => {
      void loadPublicImageThemes(force).then((items) => {
        if (!cancelled) setThemes(items);
      }).catch(() => undefined);
    };
    const refreshIfStale = () => refresh(false);
    const refreshAfterInvalidation = () => refresh(true);
    const refreshAfterStorageInvalidation = (event: StorageEvent) => {
      if (event.key === IMAGE_THEME_CATALOG_INVALIDATED_STORAGE_KEY) refresh(true);
    };
    refreshIfStale();
    const timer = window.setInterval(refreshIfStale, IMAGE_THEME_CATALOG_CACHE_TTL_MS);
    window.addEventListener("focus", refreshIfStale);
    window.addEventListener(IMAGE_THEME_CATALOG_INVALIDATED_EVENT, refreshAfterInvalidation);
    window.addEventListener("storage", refreshAfterStorageInvalidation);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshIfStale);
      window.removeEventListener(IMAGE_THEME_CATALOG_INVALIDATED_EVENT, refreshAfterInvalidation);
      window.removeEventListener("storage", refreshAfterStorageInvalidation);
    };
  }, []);
  return mergeImageThemeOptions(themeOptionsForMode("image"), themes || []);
}

export function mergeImageThemeOptions(base: ThemeOption[], themes: PublicImageTheme[]): ThemeOption[] {
  if (!themes.length) return base;
  const smart = base.filter((theme) => theme.key === "smart-detect");
  const existing = new Map(base.map((theme) => [theme.key, theme]));
  return [
    ...smart,
    ...themes.map((theme) => ({
      key: theme.code,
      mode: "image" as const,
      name: theme.name,
      description: theme.description,
      previewThemePack: existing.get(theme.code)?.previewThemePack || previewForColors(theme),
      previewImageUrl: theme.coverUrl,
    })),
  ];
}

export function loadPublicImageThemes(force = false): Promise<PublicImageTheme[]> {
  if (!force && cache && isImageThemeCatalogCacheFresh(cacheUpdatedAt)) return Promise.resolve(cache);
  if (force && request) {
    return request.then(
      () => loadPublicImageThemes(true),
      () => loadPublicImageThemes(true),
    );
  }
  request ||= fetch("/api/app/v2/image-themes", { credentials: "same-origin", cache: "no-store" })
    .then(async (response) => {
      const body = await response.json() as { ok?: boolean; themes?: PublicImageTheme[] };
      if (!response.ok || body.ok === false) throw new Error("图片主题加载失败");
      cache = body.themes || [];
      cacheUpdatedAt = Date.now();
      return cache;
    })
    .finally(() => { request = undefined; });
  return request;
}

export function invalidatePublicImageThemeCatalog(): void {
  cache = undefined;
  cacheUpdatedAt = 0;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(IMAGE_THEME_CATALOG_INVALIDATED_EVENT));
    try {
      window.localStorage.setItem(IMAGE_THEME_CATALOG_INVALIDATED_STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage may be unavailable in private or embedded browsing contexts.
    }
  }
}

export function isImageThemeCatalogCacheFresh(updatedAt: number, now = Date.now()): boolean {
  return updatedAt > 0 && now - updatedAt < IMAGE_THEME_CATALOG_CACHE_TTL_MS;
}

function previewForColors(theme: PublicImageTheme): ThemeOption["previewThemePack"] {
  const background = theme.background.replace("#", "");
  const average = background.length === 6
    ? (Number.parseInt(background.slice(0, 2), 16) + Number.parseInt(background.slice(2, 4), 16) + Number.parseInt(background.slice(4, 6), 16)) / 3
    : 255;
  if (average < 90) return "theme02";
  return theme.accent.toUpperCase().includes("D6") ? "theme04" : "theme01";
}
